'use client'

import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { Join } from 'design-studio/lib'

import './shiki-code.css'

interface ShikiCodeProps {
  code: string
  lang: string
  className?: string
  colorScheme?: 'dark' | 'light' | 'auto'
}

export function ShikiCode({
  code,
  lang,
  className = '',
  colorScheme = 'dark',
}: ShikiCodeProps) {
  const [html, setHtml] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    let cancelled = false

    import('shiki')
      .then(({ codeToHtml }) =>
        codeToHtml(code, {
          lang,
          themes: {
            light: 'github-light',
            dark: 'one-dark-pro',
          },
          defaultColor: false,
          transformers: [
            {
              pre(node) {
                delete node.properties.style
              },
            },
          ],
        }),
      )
      .then((result) => {
        if (!cancelled) setHtml(result)
      })
      .catch(console.error)
    return () => {
      cancelled = true
    }
  }, [code, lang, colorScheme])

  const schemeScope =
    colorScheme === 'dark'
      ? 'shiki-code-block dark'
      : colorScheme === 'light'
        ? 'shiki-code-block light'
        : 'shiki-code-block'

  const handleCopy = async () => {
    await navigator.clipboard.writeText(code)
    setCopied(true)
    setTimeout(() => setCopied(false), 1600)
  }

  const containerClass =
    colorScheme === 'light'
      ? 'border-slate-200 bg-white'
      : colorScheme === 'auto'
        ? 'border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-950'
        : 'border-slate-700 bg-slate-950'

  const preClass =
    colorScheme === 'light'
      ? 'text-slate-800'
      : colorScheme === 'auto'
        ? 'text-slate-800 dark:text-slate-100'
        : 'text-slate-100'

  const buttonClass =
    colorScheme === 'light'
      ? 'text-slate-400 hover:bg-slate-100 hover:text-slate-600'
      : colorScheme === 'auto'
        ? 'text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:text-slate-500 dark:hover:bg-slate-800 dark:hover:text-slate-200'
        : 'text-slate-500 hover:bg-slate-800 hover:text-slate-200'

  return (
    <div
      className={Join(
        'group relative overflow-hidden rounded-lg border',
        schemeScope,
        containerClass,
        className,
      )}
    >
      {html ? (
        <div
          className="overflow-x-auto p-4 [&_code]:text-xs [&_code]:leading-relaxed [&_pre]:bg-transparent! [&_pre]:p-0"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      ) : (
        <pre
          className={Join(
            'overflow-x-auto p-4 font-mono text-xs leading-relaxed whitespace-pre',
            preClass,
          )}
        >
          {code}
        </pre>
      )}

      <button
        onClick={handleCopy}
        className={Join(
          'absolute top-2 right-2 cursor-pointer rounded-md p-2 opacity-0 transition-all group-hover:opacity-100',
          buttonClass,
        )}
        title={copied ? 'Copied!' : 'Copy to clipboard'}
        aria-label="Copy code"
      >
        {copied ? (
          <motion.svg
            key="check"
            className="h-5 w-5 text-green-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 700, damping: 20 }}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </motion.svg>
        ) : (
          <svg
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
            />
          </svg>
        )}
      </button>
    </div>
  )
}
